import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  programasSistema,
  getTrendingProgramas,
  getMostFollowedProgramas,
  getTopRankingProgramas,
  getProgramasAlAire,
  sistemaProgramasStats,
} from '@/mocks/sistemaProgramas';
import type { ProgramaSistema } from '@/mocks/sistemaProgramas';

type SortKey = 'trending' | 'top' | 'ranking' | 'followers' | 'onair';

const sorts: { key: SortKey; label: string; icon: string }[] = [
  { key: 'trending', label: 'Tendencia', icon: 'ri-fire-line' },
  { key: 'top', label: 'Más Seguidos', icon: 'ri-heart-line' },
  { key: 'ranking', label: 'Top Ranking', icon: 'ri-trophy-line' },
  { key: 'followers', label: 'Más Engagement', icon: 'ri-bar-chart-line' },
  { key: 'onair', label: 'Al Aire', icon: 'ri-broadcast-line' },
];

export default function SistemaProgramasPage() {
  const [activeSort, setActiveSort] = useState<SortKey>('trending');
  const [viewMode, setViewMode] = useState<'grid' | 'rules'>('grid');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trending = getTrendingProgramas();
  const topRanking = getTopRankingProgramas();

  const sortedPrograms: ProgramaSistema[] = (() => {
    switch (activeSort) {
      case 'trending':
        return [...programasSistema].sort((a, b) => (b.isTrending ? 1 : 0) - (a.isTrending ? 1 : 0));
      case 'top':
        return getMostFollowedProgramas();
      case 'ranking':
        return getTopRankingProgramas();
      case 'followers':
        return [...programasSistema].sort((a, b) => b.weeklyGrowth - a.weeklyGrowth);
      case 'onair':
        return [...programasSistema].sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0));
      default:
        return programasSistema;
    }
  })();

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20radio%20broadcast%20production%20studio%20with%20colorful%20sound%20mixing%20console%2C%20multiple%20monitors%20displaying%20audio%20waveforms%2C%20warm%20atmospheric%20lighting%2C%20creative%20media%20production%20environment%2C%20modern%20broadcast%20facility%2C%20editorial%20photography%20with%20rich%20ambient%20tones%20and%20depth%20of%20field&width=1600&height=900&seq=sistema-programas-hero&orientation=landscape"
            alt="Sistema de Programas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/65"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
                </span>
                <span className="text-[10px] font-extrabold text-rose-300 tracking-widest uppercase bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full">
                  SISTEMA DE PROGRAMAS
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Cada Programa Tiene Vida Propia</h1>
              <p className="text-sm md:text-base text-white/80 max-w-2xl">
                Descubrí los programas que están dominando la plataforma. Seguí, compartí, participá y comentá. Un programa puede ser más famoso que la radio.
              </p>
              <div className="flex items-center gap-6 mt-4 text-white/90 text-xs md:text-sm flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-broadcast-line"></i>
                  {sistemaProgramasStats.totalPrograms} programas
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  {sistemaProgramasStats.onAirNow} al aire
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-group-line"></i>
                  {sistemaProgramasStats.totalFollowers} seguidores
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-white border-b border-background-200/70 sticky top-0 z-30">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 md:gap-6 overflow-x-auto">
                {[
                  { value: sistemaProgramasStats.totalPrograms.toLocaleString(), label: 'Programas' },
                  { value: sistemaProgramasStats.trendingCount.toString(), label: 'En Tendencia' },
                  { value: sistemaProgramasStats.mostFollowed.toString(), label: 'Top Seguidos' },
                  { value: sistemaProgramasStats.onAirNow.toString(), label: 'Al Aire' },
                  { value: sistemaProgramasStats.totalFollowers, label: 'Seguidores' },
                  { value: sistemaProgramasStats.categoriesCount.toString(), label: 'Categorías' },
                ].map((stat, i) => (
                  <div key={i} className="text-center flex-shrink-0">
                    <span className="text-sm md:text-base font-extrabold text-foreground-950 leading-none block">{stat.value}</span>
                    <span className="text-[9px] text-foreground-400 uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'rules' : 'grid')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-xs font-semibold text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={`${viewMode === 'grid' ? 'ri-scales-line' : 'ri-grid-line'} text-sm`}></i>
                {viewMode === 'grid' ? 'Ver Reglas' : 'Ver Grid'}
              </button>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          {viewMode === 'grid' ? (
            <>
              {/* Sorts */}
              <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
                {sorts.map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setActiveSort(sort.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                      activeSort === sort.key
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${sort.icon} text-sm`}></i>
                    {sort.label}
                  </button>
                ))}
              </div>

              {/* Trending Strip */}
              {activeSort === 'trending' && (
                <>
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                        <i className="ri-fire-line text-rose-600 text-sm"></i>
                      </div>
                      <h2 className="text-lg font-bold text-foreground-950">En Tendencia</h2>
                      <span className="text-xs text-foreground-400">{trending.length} programas</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {trending.map((programa) => (
                        <ProgramaCardLarge key={programa.programId} programa={programa} badge="trending" />
                      ))}
                    </div>
                  </div>

                  {/* Top Ranking */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <i className="ri-trophy-line text-amber-600 text-sm"></i>
                      </div>
                      <h2 className="text-lg font-bold text-foreground-950">Top Ranking</h2>
                      <span className="text-xs text-foreground-400">{topRanking.length} programas</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {topRanking.slice(0, 6).map((programa, i) => (
                        <ProgramaCardLarge key={programa.programId} programa={programa} badge="top" rank={i + 1} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Other sort views */}
              {activeSort !== 'trending' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {sortedPrograms.map((programa, i) => (
                    <ProgramaCardLarge key={programa.programId} programa={programa} badge={activeSort === 'ranking' ? 'top' : 'default'} rank={activeSort === 'ranking' ? i + 1 : undefined} />
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Rules View */
            <div>
              <h2 className="text-lg font-bold text-foreground-950 mb-5">Las 4 Reglas del Sistema</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { title: 'Un programa puede ser más famoso que la radio', desc: 'Tardes con Vale tiene más seguidores que muchas radios enteras. Los programas construyen su propia identidad, comunidad y alcance, independientemente del dial que los transmite. Un programa exitoso trasciende su frecuencia.', icon: 'ri-star-line', color: 'from-rose-500 to-rose-400' },
                  { title: 'Cada programa tiene vida propia', desc: 'Perfil completo con portada, descripción, horarios, locutores, seguidores, ranking, posts, videos, eventos, concursos, club de fans y top fans. Todo lo que un programa necesita para brillar en la plataforma.', icon: 'ri-heart-pulse-line', color: 'from-amber-500 to-amber-400' },
                  { title: 'Los oyentes siguen programas, no solo radios', desc: 'Un oyente puede ser fan del programa matutino de Radio Éxito y del nocturno de Estéreo Capital. La relación es oyente-contenido, no oyente-dial. RFW entiende que la lealtad está en el contenido.', icon: 'ri-user-heart-line', color: 'from-emerald-500 to-emerald-400' },
                  { title: 'El ranking mide la relevancia real', desc: 'Combinamos seguidores, escuchas, interacciones en publicaciones, participación en concursos, actividad del club de fans y crecimiento semanal para determinar qué programas dominan la plataforma.', icon: 'ri-line-chart-line', color: 'from-sky-500 to-sky-400' },
                ].map((regla, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-foreground-100 p-6 flex items-start gap-5">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${regla.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground-950 text-base leading-snug">{regla.title}</h3>
                      <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{regla.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                {[
                  { value: sistemaProgramasStats.totalPrograms.toLocaleString(), label: 'Programas Activos' },
                  { value: sistemaProgramasStats.totalFollowers, label: 'Seguidores Totales' },
                  { value: sistemaProgramasStats.onAirNow.toString(), label: 'Al Aire Ahora' },
                  { value: sistemaProgramasStats.categoriesCount.toString(), label: 'Categorías' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl border border-foreground-100 p-4 text-center">
                    <span className="text-xl md:text-2xl font-extrabold text-foreground-950 block">{stat.value}</span>
                    <span className="text-[10px] text-foreground-400 uppercase tracking-wider">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="py-10 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <div className="bg-gradient-to-r from-rose-500 to-amber-500 rounded-2xl p-8 md:p-10 inline-block mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2">¿Tenés un programa?</h2>
              <p className="text-white/90 text-sm max-w-md mx-auto mb-5">
                Sumá tu programa a la plataforma. Creá su perfil, conectá con oyentes y hacé que tu contenido tenga vida propia.
              </p>
              <Link
                to="/register-radio"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-rose-600 font-bold rounded-xl hover:bg-rose-50 transition-all cursor-pointer text-sm"
              >
                <i className="ri-add-circle-line"></i>
                Crear mi programa
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Spacer for global player */}
      <div className="h-14 md:h-16"></div>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
  return n.toString();
}

function ProgramaCardLarge({
  programa,
  badge,
  rank,
}: {
  programa: ProgramaSistema;
  badge: 'trending' | 'top' | 'default';
  rank?: number;
}) {
  return (
    <Link
      to={`/program/${programa.slug}`}
      className="group bg-white rounded-2xl border border-foreground-100 overflow-hidden hover:shadow-sm transition-all cursor-pointer flex flex-col"
    >
      {/* Cover */}
      <div className="relative w-full h-44 overflow-hidden">
        <img
          src={programa.coverUrl}
          alt={programa.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Live indicator */}
        {programa.isActive && (
          <div className="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            EN VIVO
          </div>
        )}

        {/* Trending badge */}
        {badge === 'trending' && (
          <div className="absolute top-3 right-3 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <i className="ri-fire-line"></i>
            TENDENCIA
          </div>
        )}

        {/* Rank badge */}
        {rank && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <span className="text-xs font-extrabold">#{rank}</span>
          </div>
        )}

        {/* Host + Radio overlay */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-white/30 flex-shrink-0">
            <img src={programa.hostPhotoUrl} alt={programa.hostName} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-xs font-semibold truncate">{programa.hostName}</p>
            <p className="text-white/70 text-[10px] truncate">{programa.radioName} {programa.radioFrequency}</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{programa.category}</span>
          <span className="text-[10px] text-foreground-400">{programa.schedule}</span>
        </div>

        <h3 className="font-bold text-foreground-950 text-sm group-hover:text-accent-600 transition-colors mb-1.5">
          {programa.name}
        </h3>

        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3 flex-1">
          {programa.description}
        </p>

        {/* Tags */}
        <div className="flex items-center gap-1 flex-wrap mb-3">
          {programa.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[9px] bg-background-100 text-foreground-500 px-1.5 py-0.5 rounded-full">
              #{tag}
            </span>
          ))}
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <div className="flex items-center gap-3 text-[10px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-group-line text-[10px]"></i>
              {formatNumber(programa.followers)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-trophy-line text-[10px]"></i>
              #{programa.rankingPosition}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line text-[10px]"></i>
              {formatNumber(programa.clubMembers)}
            </span>
          </div>
          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600">
            <i className="ri-arrow-up-line"></i>
            {programa.weeklyGrowth}%
          </span>
        </div>
      </div>
    </Link>
  );
}